/** sRGB relative luminance of `#RRGGBB` (WCAG 2.x). */
export function luminance(hex: string): number {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) throw new Error(`Not a #RRGGBB color: ${hex}`);
  const [r, g, b] = match.slice(1).map((part) => {
    const c = parseInt(part, 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white, whichever reads better on `hex` (e.g. a check mark on a swatch). */
export function readableOn(hex: string): '#000000' | '#FFFFFF' {
  return contrastRatio('#000000', hex) >= contrastRatio('#FFFFFF', hex) ? '#000000' : '#FFFFFF';
}

type Vec3 = [number, number, number];

function toLinear(hex: string): Vec3 {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) throw new Error(`Not a #RRGGBB color: ${hex}`);
  return match.slice(1).map((part) => {
    const c = parseInt(part, 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Vec3;
}

function toLab([r, g, b]: Vec3): Vec3 {
  // Linear sRGB → XYZ (D65) → CIELAB.
  const x = (0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047;
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const z = (0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883;
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 / 116) * t + 16 / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

/** Machado et al. (2009) simulation matrices, full severity, linear RGB. */
const VISION: Record<ColorVision, readonly Vec3[] | null> = {
  typical: null,
  protanopia: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deuteranopia: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
  tritanopia: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.3039],
  ],
};

export type ColorVision = 'typical' | 'protanopia' | 'deuteranopia' | 'tritanopia';

/** Perceptual distance (CIE76 ΔE) between two colors as seen with `vision`. */
export function colorDistance(a: string, b: string, vision: ColorVision = 'typical'): number {
  const matrix = VISION[vision];
  const simulate = (rgb: Vec3): Vec3 =>
    matrix
      ? (matrix.map((row) =>
          Math.min(1, Math.max(0, row[0] * rgb[0] + row[1] * rgb[1] + row[2] * rgb[2])),
        ) as Vec3)
      : rgb;
  const [l1, a1, b1] = toLab(simulate(toLinear(a)));
  const [l2, a2, b2] = toLab(simulate(toLinear(b)));
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

/** Below this ΔE two swatches are easy to confuse (the v2 defaults all stay above it). */
export const CONFUSABLE_DELTA_E = 10;

export interface ConfusablePair {
  a: number;
  b: number;
  vision: ColorVision;
}

/** Pairs of colors (by index) that are hard to tell apart, typically or with a color vision deficiency. */
export function confusablePairs(colors: readonly string[]): ConfusablePair[] {
  const pairs: ConfusablePair[] = [];
  for (let a = 0; a < colors.length; a++) {
    for (let b = a + 1; b < colors.length; b++) {
      const vision = (Object.keys(VISION) as ColorVision[]).find(
        (v) => colorDistance(colors[a] ?? '', colors[b] ?? '', v) < CONFUSABLE_DELTA_E,
      );
      if (vision) pairs.push({ a, b, vision });
    }
  }
  return pairs;
}
